/* eslint-disable react/style-prop-object */
import React, { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { delay } from "helpers/app";

import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import { externalDatabaseSteps } from "constants/utility";

import SectionsBreadCrumbs from "components/shared/breadCrumbs/SectionsBreadCrumbs";
import AppModal from "components/shared/modal/AppModal";
import UserWaiting from "components/shared/progressBar/UserWaiting";

import { postCreateExternalConnection } from "redux/handlers/dockerHandler";
import { getServerByType } from "redux/handlers/utilityHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { IServerByTypeAPI } from "types/api/serversApiInterface";

import ExternalDatabaseContent from "./databaseSections/ExternalDatabaseContent";
import ExternalDatabaseRecap from "./databaseSections/ExternalDatabaseRecap";

const ExternalDatabaseWizard = (): ReactElement => {
 const intl = useIntl();
 const navigate = useNavigate();
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const [activeStep, setActiveStep] = useState<number>(0);
 const [openModal, setOpenModal] = useState<boolean>(false);
 const [isLoading, setIsLoading] = useState<boolean>(true);
 const [loading, setLoading] = useState<boolean>(false);
 const [servers, setServers] = useState<Array<IServerByTypeAPI>>([]);

 useEffect(() => {
  (async () => {
   setServers(await dispatch(getServerByType("container")));
   setIsLoading(false);
  })();
 }, []);

 const { control, formState, reset, watch, setValue } = useForm({
  defaultValues: {
   server: 0,
   upload_limit: 0,
   db_host: "",
   port: 9089,
   db_name: ""
  }
 });

 const handleOpen = () => setOpenModal(true);
 const handleClose = () => setOpenModal(false);
 const handleReset = () => {
  reset();
  setActiveStep(0);
 };

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(
   postCreateExternalConnection(
    watch("server"),
    watch("db_host"),
    watch("port"),
    watch("db_name"),
    watch("upload_limit")
   )
  );
  await delay(3000);
  setLoading(false);
  handleClose();
  handleReset();
  navigate("/utility/databases");
 };

 const setDataValue = (
  type: "server" | "db_host" | "port" | "db_name" | "upload_limit",
  value: string | number
 ) => {
  switch (type) {
   case "server":
    setValue("server", value as number);
    break;
   case "db_host":
    setValue("db_host", value as string);
    break;
   case "port":
    setValue("port", value as number);
    break;
   case "db_name":
    setValue("db_name", value as string);
    break;
   case "upload_limit":
    setValue("upload_limit", value as number);
    break;
   default:
    break;
  }
 };

 const checkActiveStep = (index: number): boolean => {
  switch (index) {
   case 0:
    return watch("server") !== 0;
   case 1:
    return watch("upload_limit") >= 0;
   case 2:
    return watch("db_host") !== "";
   case 3:
    return watch("port") > 1024 || watch("port") < 65535;
   case 4:
    return watch("db_name") !== "";
   default:
    return false;
  }
 };

 return !isLoading ? (
  <Container maxWidth={false} sx={{ mx: 2, width: "auto" }}>
   <Stack mt={2}>
    <SectionsBreadCrumbs
     links={[
      { name: "home", url: "/" },
      { name: "utility", url: "/utility" },
      { name: "databases", url: "/utility/databases" },
      { name: "add", url: "/utility/databases/add" }
     ]}
    />
   </Stack>
   <Grid container spacing={3} sx={{ mt: 3 }}>
    <Grid xs={desktopViewPort ? 3 : 12} sx={{ textAlign: "left" }}>
     <Stack sx={{ position: "sticky", top: 20 }}>
      <Typography variant="unboundedTitle" fontSize={18} fontWeight="bold">
       <FormattedMessage id={"utility.externalDatabases.addDatabase"} />
      </Typography>
      {desktopViewPort && (
       <Stepper activeStep={activeStep} orientation="vertical">
        {externalDatabaseSteps.map((step, index) => (
         <Step key={step.label} completed={checkActiveStep(index)}>
          <StepLabel>
           <FormattedMessage id={step.label} />
          </StepLabel>
         </Step>
        ))}
       </Stepper>
      )}
     </Stack>
    </Grid>
    <Grid xs={desktopViewPort ? 6 : 12}>
     <ExternalDatabaseContent
      control={control}
      watch={watch}
      formState={formState}
      servers={servers}
      handleOpen={handleOpen}
      handleReset={handleReset}
      setValue={(type, value) => setDataValue(type, value)}
     />
    </Grid>
    <Grid xs={desktopViewPort ? 3 : 12}>
     <ExternalDatabaseRecap
      server={servers.find((element) => element.id === watch("server"))?.server_name || ""}
      database={watch("db_name")}
      host={watch("db_host")}
      port={watch("port")}
      uploadLimit={watch("upload_limit")}
     />
    </Grid>
   </Grid>
   <AppModal
    open={openModal}
    close={handleClose}
    title={intl.formatMessage({ id: "utility.externalDatabases.add.createNewDatabase" })}
    handleClose={handleClose}
    disabled={loading}
    handleConfirm={handleConfirm}>
    <Stack>
     <FormattedMessage id="utility.externalDatabases.add.confirmCreation" />
    </Stack>
   </AppModal>
   <UserWaiting openLoader={loading} />
  </Container>
 ) : (
  <></>
 );
};

export default ExternalDatabaseWizard;
